import { arrayAdd, arrayRemove } from '@/helpers/array';
import useForm from '@/hooks/useForm/useForm';
import useModal from '@/hooks/useModal/useModal';
import { useQueryParams } from '@/hooks/useQueryParams/useQueryParams';
import React, { useEffect } from 'react';
import { BoxWidth } from '../Box/consts';
import { ButtonType } from '../Button/consts';
import ButtonSubmit from '../ButtonSubmit/ButtonSubmit';
import CheckBoxBlockGroup from '../CheckBoxCardGroup/CheckBoxCardGroup';
import FormField from '../FormField/FormField';
import FormFieldMultiSelect from '../FormFieldMultiSelect/FormFieldMultiSelect';
import FormRow from '../FormRow/FormRow';
import Headline from '../Headline/Headline';
import { HeadlineColor, HeadlineLevel } from '../Headline/consts';
import ModalCloseIcon from '../Modal/ModalCloseIcon/ModalCloseIcon';
import ModalContent from '../Modal/ModalContent';
import RichText from '../RichText/RichText';
import Spacer from '../Spacer/Spacer';
import { MAIN_JOB_WORKING_WORLD_CATEGORIES } from './const';
import type { JobAlertModalContentProps } from './interfaces';
import jobAlertModalContentSchema from './schema';
import transformQueryParams from './transformQueryParams';

const INITIAL_JOB_ALERT_MODAL_CONTENT_STATE = {
  email: '',
  timeType: [],
  categories: [],
  token: '',
  isEmailEditable: true,
};

const mainJobWorkingWorldCategories = MAIN_JOB_WORKING_WORLD_CATEGORIES.map(
  (option) => ({
    ...option,
    value: option.label,
  })
);
const mainJobWorkingWorldCategoryLabels = MAIN_JOB_WORKING_WORLD_CATEGORIES.map(
  ({ label }) => label
);

const JobAlertModalContent = ({
  onSubmit,
  buttonSubmitState,
}: JobAlertModalContentProps) => {
  const { params } = useQueryParams();

  const isSearchParamsUsedRef = React.useRef(false);

  const {
    formState,
    getErrorByFieldName,
    handleBlur,
    handleChange,
    handleSubmit,
    isFormValid,
    setFormState,
    setFieldValue,
  } = useForm(
    onSubmit,
    jobAlertModalContentSchema,
    INITIAL_JOB_ALERT_MODAL_CONTENT_STATE,
    'job-alert'
  );
  const { closeModal } = useModal();

  const isWorkingTimeSelected = (name: string): boolean => {
    return (formState['timeType'] as string[]).some((key) => key === name);
  };

  const handleWorkingTimeChange = (
    $event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked, name } = $event.target;
    const selectedTimeTypes = formState['timeType'];

    if (checked && !isWorkingTimeSelected(name)) {
      setFieldValue('timeType', arrayAdd(selectedTimeTypes, name));
    } else {
      setFieldValue('timeType', arrayRemove(selectedTimeTypes, name));
    }
  };

  const handleOnChangeWorkingWorld = (
    selectedValues: readonly string[] | undefined
  ) => {
    setFieldValue('categories', selectedValues);
  };

  const { token, email, categories, timeType } = transformQueryParams(params);

  useEffect(() => {
    if (
      !isSearchParamsUsedRef.current &&
      (token || email || categories || timeType)
    ) {
      const filteredCategoriesByMainCategoryLabels = categories?.filter(
        (category) => mainJobWorkingWorldCategoryLabels.includes(category)
      );

      setFormState({
        ...INITIAL_JOB_ALERT_MODAL_CONTENT_STATE,
        ...(token && { token }),
        ...(email && { email }),
        ...(filteredCategoriesByMainCategoryLabels && {
          categories: filteredCategoriesByMainCategoryLabels,
        }),
        ...(timeType && { timeType }),
      });

      isSearchParamsUsedRef.current = true;
    }
  }, [categories, email, params, setFormState, timeType, token]);

  const isUserAuthenticated = !!(formState['token'] && formState['email']);

  return (
    <ModalContent width={BoxWidth.SMedium}>
      <ModalCloseIcon onClick={closeModal} />
      <Headline level={HeadlineLevel.Four} color={HeadlineColor.Dark}>
        {isUserAuthenticated
          ? 'Job-Alert Angaben ändern'
          : 'Kein Job-Angebot mehr verpassen!'}
      </Headline>
      <Spacer defaultSize={1} sm={2} />
      <RichText align="left" size="small">
        Lass dich von uns per E-Mail benachrichtigen, sobald ein passender Job
        verfügbar ist.
      </RichText>
      <Spacer defaultSize={1.5} />
      <form onSubmit={handleSubmit}>
        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <FormFieldMultiSelect
            selectedValues={formState['categories']}
            initialSelectedValues={formState['categories']}
            errorMessage={getErrorByFieldName('categories')}
            errorMessageHasRelativePosition
            onBlur={handleBlur}
            options={mainJobWorkingWorldCategories}
            name="workingWorldCategories"
            onChange={handleOnChangeWorkingWorld}
            label="Arbeitswelten"
          />
        </FormRow>
        <FormRow bottomMarginSize={{ default: 0.75 }}>
          <CheckBoxBlockGroup
            options={[
              {
                label: 'Vollzeit',
                name: 'full-time',
                checked: isWorkingTimeSelected('full-time'),
              },
              {
                label: 'Teilzeit',
                name: 'part-time',
                checked: isWorkingTimeSelected('part-time'),
              },
            ]}
            errorMessage={getErrorByFieldName('timeType')}
            onChange={handleWorkingTimeChange}
          />
        </FormRow>
        <FormRow bottomMarginSize={{ default: 1.5, xs: 2 }}>
          <FormField
            autoComplete="email"
            label="E-Mail-Adresse"
            name={'email'}
            value={formState['email'] || ''}
            onBlur={handleBlur}
            onChange={handleChange}
            errorMessage={getErrorByFieldName('email')}
            id={'email'}
            disabled={isUserAuthenticated}
            errorMessageHasRelativePosition
          />
        </FormRow>

        <ButtonSubmit
          state={buttonSubmitState}
          inactive={!isFormValid}
          type={ButtonType.Submit}
          stateMessages={{
            pending: 'Senden …',
            fail: 'Fehlgeschlagen',
            success: isUserAuthenticated
              ? 'Erfolgreich angepasst!'
              : 'E-Mail versendet!',
          }}
        >
          {isUserAuthenticated
            ? 'Neue Auswahl speichern'
            : 'Job-Alert aktivieren'}
        </ButtonSubmit>
      </form>
    </ModalContent>
  );
};

export default JobAlertModalContent;
